import { API_BASE_URL } from "../../constant/constant";
import React, { useState } from "react";
import { Button, Input, Select, Upload, Form, Row, Col, message } from "antd";
import { useTranslation } from "react-i18next";
import { InboxOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import BackIcon from "../../assets/back-icon.svg";
import { Container, Content, Header, Heading } from "./Style";
import { handleFileUploadWithSignedURL } from "../Environment/Asconfig";
import { toast } from "react-toastify";
import { useSnackbar } from "../../context/SnackBarContext";

const { TextArea } = Input;
const { Dragger } = Upload;
const { Option } = Select;

const uploadFileToS3 = async (file) => {
  try {
    const response = await handleFileUploadWithSignedURL(file);
    return response.fileUrl;
  } catch (error) {
    toast.error(error);
    return null;
  }

};

export default function AddSkills() {
  const { t } = useTranslation();
  const { isSidebarOpen } = useOutletContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const showSnackbar = useSnackbar();

  // Handle file selection in the Dragger component
  const handleFileChange = ({ fileList }) => {
    setSelectedFiles(fileList.map((file) => file.originFileObj));
  };

  const handleFinish = async (values) => {
    setLoading(true);
    const fileUrls = [];
    const id = localStorage.getItem("id");

    // Upload files to S3 and get URLs
    for (const file of selectedFiles) {
      try {
        const fileUrl = await uploadFileToS3(file);
        if (!fileUrl) throw new Error("File upload failed");
        fileUrls.push({
          fileUrl,
          fileName: file.name,
          fileSize: file.size,
          uploadTime: new Date().toISOString(),
        });

        // Add notification for successful file upload
        try {
          await axios.post(`${API_BASE_URL}/api/notification`, {
            recipientId: localStorage.getItem("id"),
            senderId: "Skills App",
            title: t("billing.addSkills.notifications.uploadSuccess.title"),
            content: t("billing.addSkills.notifications.uploadSuccess.content", { fileName: file.name }),
            urgency: "normal",
          }, {
            headers: {
              "Content-Type": "application/json",
              "x-auth-token": `${localStorage.getItem("token")}`,
            },
          });
        } catch (notifError) {
          if (notifError.response?.data?.responseCode === 429) {
            showSnackbar(notifError.response.data.message, "warning");
          }
        }
      } catch (error) {
        console.error("Error uploading file", error);
        message.error(t("billing.addSkills.notifications.uploadFailed.simple", { fileName: file.name }));

        // Add notification for failed file upload
        try {
          await axios.post(`${API_BASE_URL}/api/notification`, {
            recipientId: localStorage.getItem("id"),
            senderId: "Skills App",
            title: t("billing.addSkills.notifications.uploadFailed.title"),
            content: t("billing.addSkills.notifications.uploadFailed.content", { fileName: file.name }),
            urgency: "high",
          }, {
            headers: {
              "Content-Type": "application/json",
              "x-auth-token": `${localStorage.getItem("token")}`,
            },
          });
        } catch (notifError) {
          if (notifError.response?.data?.responseCode === 429) {
            showSnackbar(notifError.response.data.message, "warning");
          }
        }
      }
    }

    // Prepare data to send to the final endpoint
    const payload = {
      ...values,
      user: id,
      files: fileUrls, // Include file URLs with file data
    };

    try {
      // Submit form data along with uploaded file URLs
      const response = await axios.post(
        `${API_BASE_URL}/api/skill/`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": `${localStorage.getItem("token")}`,
          },
        },
      );


      console.log("response", response.data.data._id);
      // Send success notification for skill creation
      try {
        await axios.post(`${API_BASE_URL}/api/notification`, {
          recipientId: localStorage.getItem("id"),
          senderId: "Skills App",
          title: t("billing.addSkills.notifications.skillSuccess.title"),
          content: t("billing.addSkills.notifications.skillSuccess.content", { name: values.name }),
          urgency: "normal",
        }, {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": `${localStorage.getItem("token")}`,
          },
        });
      } catch (notifError) {
        if (notifError.response?.data?.responseCode === 429) {
          showSnackbar(notifError.response.data.message, "warning");
        }
      }

      // New API call for embeddings
      const uploadedUrls = response.data.data.files.map((file) => file.fileUrl);
      try {
        const res = await axios.post(
          "https://flowproai.aivoria.nl/ai_flow_pro/create_skill_docs_embedding",
          {
            user_id: response.data.data.user,
            skill_id: response.data.data._id,
            uploaded_urls: uploadedUrls,
          }
        );

        // Success notification for embeddings
        try {
          await axios.post(`${API_BASE_URL}/api/notification`, {
            recipientId: localStorage.getItem("id"),
            senderId: "Skills App",
            title: t("billing.addSkills.notifications.embeddingsSuccess.title"),
            content: t("billing.addSkills.notifications.embeddingsSuccess.content", { name: values.name }),
            urgency: "normal",
          }, {
            headers: {
              "Content-Type": "application/json",
              "x-auth-token": `${localStorage.getItem("token")}`,
            },
          });
        } catch (notifError) {
          if (notifError.response?.data?.responseCode === 429) {
            showSnackbar(notifError.response.data.message, "warning");
          }
        }
      } catch (embeddingError) {
        console.error("Failed to create embeddings", embeddingError);

        // Add notification for failed embeddings creation
        try {
          await axios.post(`${API_BASE_URL}/api/notification`, {
            recipientId: localStorage.getItem("id"),
            senderId: "Skills App",
            title: t("billing.addSkills.notifications.embeddingsFailed.title"),
            content: t("billing.addSkills.notifications.embeddingsFailed.content", { name: values.name }),
            urgency: "high",
          }, {
            headers: {
              "Content-Type": "application/json",
              "x-auth-token": `${localStorage.getItem("token")}`,
            },
          });
        } catch (notifError) {
          if (notifError.response?.data?.responseCode === 429) {
            showSnackbar(notifError.response.data.message, "warning");
          }
        }
      }

      message.success(t("billing.addSkills.notifications.skillSuccess.simple"));
      navigate(`/run-skills/${response.data.data._id}`); // Navigate to run-skills with the new skill's ID
    } catch (error) {
      console.error("Failed to create skill", error);

      // Send failure notification
      try {
        await axios.post(`${API_BASE_URL}/api/notification`, {
          recipientId: localStorage.getItem("id"),
          senderId: "Skills App",
          title: t("billing.addSkills.notifications.skillFailed.title"),
          content: t("billing.addSkills.notifications.skillFailed.content", { name: values.name }),
          urgency: "high",
        }, {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": `${localStorage.getItem("token")}`,
          },
        });
      } catch (notifError) {
        if (notifError.response?.data?.responseCode === 429) {
          showSnackbar(notifError.response.data.message, "warning");
        }
      }

      message.error(t("billing.addSkills.notifications.skillFailed.simple"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container isOpen={isSidebarOpen}>
      <Header onClick={() => navigate("/skills")}>
        <img src={BackIcon} alt="back" />
        <Heading>{t("billing.addSkills.title")}</Heading>
      </Header>
      <Content>
        <Form
          style={{ padding: "36px 36px" }}
          layout="vertical"
          onFinish={handleFinish}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.name")}</span>}
                name="name"
                rules={[{ required: true, message: t("billing.addSkills.validation.name") }]}
              >
                <Input placeholder={t("billing.addSkills.placeholders.name")} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.model")}</span>}
                name="model"
                rules={[{ required: true, message: t("billing.addSkills.validation.model") }]}
              >
                <Select placeholder={t("billing.addSkills.placeholders.model")}>
                  <Option value="gpt_3.5">{t("billing.addSkills.models.gpt35")}</Option>
                  <Option value="gpt_4o">{t("billing.addSkills.models.gpt4o")}</Option>
                  <Option value="gpt_4o_mini">{t("billing.addSkills.models.gpt4oMini")}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.objectives")}</span>}
                name="objectives"
                rules={[{ required: true, message: t("billing.addSkills.validation.objectives") }]}
              >
                <TextArea placeholder={t("billing.addSkills.placeholders.description")} rows={4} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.taskGuidelines")}</span>}
                name="taskGuidelines"
              >
                <TextArea placeholder={t("billing.addSkills.placeholders.description")} rows={4} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.requiredOutput")}</span>}
                name="requiredOutput"
                rules={[
                  { required: true, message: t("billing.addSkills.validation.requiredOutput") },
                ]}
              >
                <TextArea placeholder={t("billing.addSkills.placeholders.description")} rows={4} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.otherRules")}</span>
                }
                name="otherRules"
              >
                <TextArea placeholder={t("billing.addSkills.placeholders.description")} rows={4} />
              </Form.Item>
            </Col>
          </Row>

          {/* Add new Query text area */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.query")}</span>}
                name="query"
              >
                <TextArea placeholder={t("billing.addSkills.placeholders.query")} rows={4} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<span style={{ fontWeight: 600 }}>{t("billing.addSkills.form.uploadFiles")}</span>}
            name="files"
          >
            <Dragger
              name="files"
              multiple
              accept=".pdf"
              beforeUpload={() => false}
              onChange={handleFileChange}
              fileList={selectedFiles.map((file, index) => ({
                uid: index,
                name: file.name,
                status: "done",
                originFileObj: file,
              }))}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {t("billing.addSkills.upload.dragDrop")} <a>{t("billing.addSkills.upload.browse")}</a>
              </p>
              <p className="ant-upload-hint">{t("billing.addSkills.upload.support")}</p>
            </Dragger>
          </Form.Item>

          <Form.Item style={{ display: "flex", justifyContent: "center" }}>
            <Button type="primary" htmlType="submit" loading={loading}
              style={{
                backgroundColor: '#51307F'
              }}>
              {loading ? t("billing.addSkills.buttons.saving") : t("billing.addSkills.buttons.save")}
            </Button>
          </Form.Item>
        </Form>
      </Content>
    </Container>
  );
}
